import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BIBLE_BOOKS } from '../utils/bibleData';
import { PencilSquareIcon, MicrophoneIcon } from '@heroicons/react/24/outline';

const ShareableNoteView = () => {
  const { userId, noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch the note
  useEffect(() => {
    const fetchNote = async () => {
      if (!userId || !noteId) {
        setError('Invalid note link');
        setLoading(false);
        return;
      }

      try {
        const noteDoc = await getDoc(doc(db, 'users', userId, 'notes', noteId));

        if (!noteDoc.exists()) {
          setError('Note not found or no longer available');
          setLoading(false);
          return;
        }

        const noteData = noteDoc.data();

        // Only notes explicitly shared by their author are publicly viewable
        if (!noteData.isShared) {
          setError('This note is private and not available to view.');
          setLoading(false);
          return;
        }

        setNote({
          id: noteDoc.id,
          ...noteData
        });
      } catch (error) {
        console.error('❌ Error fetching note:', error);
        setError('Failed to load note. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [userId, noteId]);

  // Get book name from ID
  const getBookName = (bookId) => {
    const book = BIBLE_BOOKS.find(b => b.id === bookId);
    return book ? book.name : bookId;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    const noteTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - noteTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return noteTime.toLocaleDateString();
  };

  // Get verse display
  const getVerseDisplay = () => {
    if (note.verseRange) {
      return `${note.verseRange.start}-${note.verseRange.end}`;
    } else if (note.verse) {
      return note.verse.toString();
    }
    return 'Chapter';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean mb-4"></div>
          <p className="text-gray-600">Loading note...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Note Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors"
          >
            Go to Lineage Bible
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-space-cadet text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Family Study Note</h1>
              <p className="text-sm opacity-90 mt-1">
                {getBookName(note.book)} {note.chapter}:{getVerseDisplay()}
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
            >
              Go to App
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Note Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cerulean bg-opacity-10 rounded-full flex items-center justify-center">
                <span className="text-cerulean font-semibold text-sm">
                  {note.userDisplayName ? note.userDisplayName.charAt(0).toUpperCase() : 'F'}
                </span>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">
                  {note.userDisplayName || note.userName || 'Family Member'}
                </h2>
                <p className="text-sm text-gray-500">
                  {formatRelativeTime(note.createdAt)}
                </p>
              </div>
            </div>
            
            {/* Note Type Badge */}
            <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${
              note.noteType === 'written'
                ? 'bg-cerulean bg-opacity-10 text-cerulean'
                : 'bg-cerulean bg-opacity-10 text-cerulean'
            }`}>
              {note.noteType === 'written'
                ? <><PencilSquareIcon className="w-4 h-4" /> Written Note</>
                : <><MicrophoneIcon className="w-4 h-4" /> Audio Note</>}
            </span>
          </div>

          {/* Verse Reference */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Scripture Reference</h3>
            <p className="text-lg text-space-cadet">
              {getBookName(note.book)} {note.chapter}
              {note.verse && `:${note.verse}`}
              {note.verseRange && `:${note.verseRange.start}-${note.verseRange.end}`}
            </p>
          </div>

          {/* Note Content */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Note Content</h3>
            {note.noteType === 'written' ? (
              <div className="prose max-w-none">
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {note.content}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <svg className="w-8 h-8 text-cerulean" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900">Audio Note</p>
                    <p className="text-sm text-gray-600">
                      Duration: {note.audioDuration ? `${Math.round(note.audioDuration)} seconds` : 'Unknown'}
                    </p>
                  </div>
                </div>
                {note.audioUrl && (
                  <audio controls className="w-full" playsInline>
                    <source src={note.audioUrl} type={note.audioType || 'audio/mp4'} />
                    Your browser does not support the audio element.
                  </audio>
                )}
              </div>
            )}
          </div>

          {/* Share Info */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-500 text-center">
              This note was shared from the Lineage Bible
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareableNoteView;
